/**
 * Adapters.
 *
 * The app's existing state predates the sales and purchase modules: it stores a
 * single flat `Invoice` type for both directions, and `Party` rather than
 * `Partner`. Rather than migrate that state in one risky step, these functions
 * translate it on read, so the new workspaces run against real data today while
 * the underlying store is migrated at its own pace.
 *
 * Translation is one-way and lossy in places (the old model has no order or
 * delivery chain), and every such gap is noted where it occurs.
 */

import { GST_STATE_CODES, type Partner } from "./partner";
import { defaultSalesConfig, type SalesConfig, type SalesDocument, type SalesLine, type SupplierDetails } from "./sales";
import { defaultPurchaseConfig, type PurchaseConfig, type PurchaseDocument, type PurchaseLine } from "./purchase";
import type { AppState, Invoice, InvoiceLine, Party, SetupConfig } from "./types";

/** Reverse lookup of the two-digit GST state code from a state name. */
const STATE_NAME_TO_CODE = new Map(
  Object.entries(GST_STATE_CODES).map(([code, name]) => [name.toLowerCase(), code])
);

export const stateCodeFor = (input?: string): string => {
  if (!input) return "29";
  const trimmed = input.trim();
  // Already a code, or a "29 · Karnataka" style label.
  const leading = trimmed.match(/^(\d{2})\b/);
  if (leading && GST_STATE_CODES[leading[1]]) return leading[1];
  const byName = STATE_NAME_TO_CODE.get(trimmed.toLowerCase());
  if (byName) return byName;
  // Fall back to the GSTIN prefix if the caller passed one.
  if (/^\d{2}[A-Z]{5}/.test(trimmed)) return trimmed.slice(0, 2);
  return "29";
};

/** Old tax treatments map onto the richer set the modules use. */
const treatmentFor = (value: InvoiceLine["taxTreatment"]): SalesLine["taxTreatment"] => {
  switch (value) {
    case "RCM": return "reverse-charge";
    case "Exempt": return "exempt";
    case "Out of scope": return "non-gst";
    default: return "taxable";
  }
};

const toLine = (line: InvoiceLine): SalesLine => ({
  id: line.id,
  description: line.description || line.item,
  hsnSac: line.hsnSac || undefined,
  uqc: line.unit||"NOS",itemId:line.itemId,taxInclusive:line.taxInclusive,priceDiscount:line.discount,lineKind:line.lineKind,
  quantity: line.quantity,
  unitPrice: line.rate,
  gstRate: line.taxRate,
  taxTreatment: treatmentFor(line.taxTreatment),
  accountCode: line.accountCode,
  costCentre: line.costCenter
});

/** The old status vocabulary mapped onto the document state machines. */
const salesStatusFor = (status: Invoice["status"]): SalesDocument["status"] => {
  switch (status) {
    case "Draft": return "Draft";
    case "Pending review": return "Pending review";
    case "Approved": return "Approved";
    case "Posted": return "Posted";
    case "Paid": return "Paid";
    default: return "Posted";
  }
};

export const toPartner = (party: Party): Partner => ({
  id: party.id,
  code: `PTR-${party.id.slice(-5)}`,
  name: party.name,
  kind: "business",
  roles: party.type === "Customer" ? ["customer"] : party.type === "Vendor" ? ["vendor"] : ["customer", "vendor"],
  gstin: party.gstin,
  // The old model has no registration status, so it is inferred: a GSTIN on
  // file means registered, its absence means unregistered.
  gstStatus: party.gstin ? "regular" : "unregistered",
  pan: party.pan,
  billingAddress: { state: party.state, stateCode: stateCodeFor(party.state) },
  active: party.active,
  currency: "INR",
  createdAt: new Date().toISOString()
});

export const toPartners = (parties: Party[]): Partner[] => parties.map(toPartner);

/**
 * The old invoice stores the party by *name*, while partners are keyed by id.
 * Resolving through this map is what lets the ledger and ageing views join to a
 * partner at all — matching on the raw name silently produces empty reports.
 */
export const toSalesDocument = (
  invoice: Invoice,
  supplierStateCode: string,
  partnerIdByName: Map<string, string> = new Map()
): SalesDocument => {
  const placeOfSupply = stateCodeFor(invoice.placeOfSupply || invoice.partyGstin);
  const partnerId = invoice.partyId??partnerIdByName.get(invoice.party.trim().toLowerCase()) ?? invoice.party;
  return {
    id: invoice.id,canonical:true,adjustments:invoice.draftDetails?.adjustments,
    type: "invoice",
    number: invoice.number,
    date: invoice.date,dueDate:invoice.draftDetails?.dueDate,notes:invoice.draftDetails?.notes,
    partnerId,
    partnerName: invoice.party,
    partnerGstin: invoice.partyGstin,
    placeOfSupply,
    placeOfSupplyName: GST_STATE_CODES[placeOfSupply],
    supplyType: placeOfSupply === supplierStateCode ? "intra-state" : "inter-state",
    status: salesStatusFor(invoice.status),
    currency: invoice.currency,
    lines: invoice.lines.map(toLine),
    rcm: invoice.rcm,
    tdsSection: invoice.tdsSection,
    tdsRate: invoice.tdsRate,
    journalId: invoice.journalId,
    balance: invoice.balance,
    createdAt: invoice.date
  };
};

export const toPurchaseDocument = (
  invoice: Invoice,
  supplierStateCode: string,
  partnerIdByName: Map<string, string> = new Map()
): PurchaseDocument => {
  const base = toSalesDocument(invoice, supplierStateCode, partnerIdByName);
  return {
    ...base,
    type: "bill",
    // Supplier reference stays separate from the internal configured bill number.
    vendorDocumentNumber: invoice.draftDetails?.supplierInvoiceNumber,
    vendorDocumentDate: invoice.date,
    lines: base.lines as PurchaseLine[],
    status: base.status as PurchaseDocument["status"]
  };
};

/** Splits the flat invoice list by direction. */
export const splitDocuments = (
  invoices: Invoice[],
  supplierStateCode: string,
  partnerIdByName: Map<string, string> = new Map()
): { sales: SalesDocument[]; purchases: PurchaseDocument[] } => ({
  sales: invoices
    .filter((invoice) => invoice.type === "Sales invoice")
    .map((invoice) => toSalesDocument(invoice, supplierStateCode, partnerIdByName)),
  purchases: invoices
    .filter((invoice) => invoice.type !== "Sales invoice")
    .map((invoice) => toPurchaseDocument(invoice, supplierStateCode, partnerIdByName))
});

export const toSupplierDetails = (setup: SetupConfig): SupplierDetails => ({
  legalName: setup.legalName || setup.companyName,
  tradeName: setup.companyName,
  gstin: setup.gstin,
  pan: setup.pan,
  address: [setup.companyName, setup.state].filter(Boolean).join("\n"),
  stateCode: stateCodeFor(setup.gstin || setup.state)
});

/**
 * Sales configuration derived from the entity setup. The stage requirements
 * default to off — a business invoicing directly is the common case, and
 * turning a stage on is a deliberate configuration act.
 */
export const salesConfigFrom = (setup: SetupConfig): SalesConfig => {
  const base = defaultSalesConfig(stateCodeFor(setup.gstin || setup.state));
  const flags = setup.featureFlags ?? {};
  return {
    ...base,
    requireQuotation: Boolean((flags as Record<string, boolean>).requireQuotation),
    requireSalesOrder: Boolean((flags as Record<string, boolean>).requireSalesOrder),
    requireDeliveryNote: Boolean((flags as Record<string, boolean>).requireDeliveryNote),
    roundOff: false
  };
};

export const purchaseConfigFrom = (setup: SetupConfig): PurchaseConfig => {
  const base = defaultPurchaseConfig(stateCodeFor(setup.gstin || setup.state));
  const flags = setup.featureFlags ?? {};
  return {
    ...base,
    roundOff:false,
    requirePurchaseOrder: Boolean((flags as Record<string, boolean>).requirePurchaseOrder),
    requireGoodsReceipt: Boolean((flags as Record<string, boolean>).requireGoodsReceipt),
    enforceThreeWayMatch: Boolean((flags as Record<string, boolean>).enforceThreeWayMatch)
  };
};

/** Everything the workspaces need, derived from app state in one call. */
export const workspaceData = (state: AppState) => {
  const supplierStateCode = stateCodeFor(state.setup.gstin || state.setup.state);
  const partners = toPartners(state.parties);
  const partnerIdByName = new Map(partners.map((partner) => [partner.name.trim().toLowerCase(), partner.id]));
  const { sales, purchases } = splitDocuments(state.invoices, supplierStateCode, partnerIdByName);
  return {
    sales,
    purchases,
    partners,
    supplier: toSupplierDetails(state.setup),
    salesConfig: salesConfigFrom(state.setup),
    purchaseConfig: purchaseConfigFrom(state.setup),
    // Receipts and payments have no counterpart in the old state yet, so the
    // ledger shows invoices only until the receipt module is added.
    receipts: [],
    payments: []
  };
};

